import { useState, type HTMLAttributes } from "react";
import { ChevronDown, ChevronRight, Copy, GripVertical, Pencil, Play, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { UserPrompt } from "@/lib/composerPrompts";

export type PromptRowProps = {
  badgeKind: "excerptus" | "mine";
  title: string;
  body: string;
  description?: string | null;
  tags: string[];
  modelHint?: string | null;
  badge: string;
  onPushToChat: () => void;
  onCopy?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
};

export const PromptRow = ({
  title, body, description, tags, modelHint, badge,
  onPushToChat, onCopy, onEdit, onDelete, onDuplicate, dragHandleProps,
}: PromptRowProps) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="flex flex-col gap-2 p-3">
      <div className="flex items-start gap-2">
        {dragHandleProps && (
          <button
            type="button"
            aria-label="Drag to reorder"
            className="mt-1 cursor-grab touch-none text-muted-foreground hover:text-foreground"
            {...dragHandleProps}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-1 text-left"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
              <h3 className="truncate text-sm font-semibold">{title}</h3>
            </button>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              {badge}
            </Badge>
          </div>
          {description && (
            <p className="ml-5 line-clamp-2 text-xs text-muted-foreground">{description}</p>
          )}
          {(tags.length > 0 || modelHint) && (
            <div className="ml-5 mt-1 flex flex-wrap gap-1">
              {modelHint && (
                <Badge variant="secondary" className="text-[10px]" title="Advisory only — does not switch the chat model">
                  Suggested model: {modelHint}
                </Badge>
              )}
              {tags.map((t) => (
                <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {expanded ? (
        <pre className="ml-5 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/30 p-2 font-mono text-[11px] leading-relaxed">
{body}
        </pre>
      ) : (
        <p className="ml-5 line-clamp-2 whitespace-pre-wrap font-mono text-[11px] text-muted-foreground">
          {body}
        </p>
      )}

      <div className="ml-5 mt-1 flex flex-wrap gap-2">
        <Button size="sm" onClick={onPushToChat} title="Push prompt to AI Chat and run it">
          <Play className="h-3.5 w-3.5" />
          Push & Run
        </Button>
        {onCopy && (
          <Button size="sm" variant="outline" onClick={onCopy}>
            <Copy className="h-3.5 w-3.5" /> Copy
          </Button>
        )}
        {onDuplicate && (
          <Button size="sm" variant="outline" onClick={onDuplicate}>
            Duplicate to my prompts
          </Button>
        )}
        {onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
        )}
        {onDelete && (
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        )}
      </div>
    </Card>
  );
};

type SortableMineRowProps = {
  prompt: UserPrompt;
  children: (dragProps: HTMLAttributes<HTMLButtonElement>) => React.ReactNode;
};

export const SortableMineRow = ({ prompt, children }: SortableMineRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: prompt.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners } as HTMLAttributes<HTMLButtonElement>)}
    </div>
  );
};
